import { Popover, Text, UnstyledButton } from "@mantine/core";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

import type { ProductImageTag } from "../../api/generated/model";
import { ProductImageTag as Tags } from "../../api/generated/zod";
import classes from "./TagSelect.module.css";

/** The live form's names for the tags, and what each one is for. */
export const TAG_LABEL: Record<ProductImageTag, string> = {
  product_shot: "Product Shot",
  in_use: "In Use",
  packaging: "Packaging",
  other: "Other",
};

const TAG_DESCRIPTION: Record<ProductImageTag, string> = {
  product_shot:
    "The product alone, clean background, full product visible. Always include one — this is the default.",
  in_use: "Someone actively holding or using the product in a real-life setting.",
  packaging: "The box, bag, or outer packaging the product comes in.",
  other: "Flat-lays, styled scenes, close-ups, or any supplementary visual.",
};

/** A new slot's tag, as live: the first is the product shot, the rest "Other". */
export function defaultTag(slot: number): ProductImageTag {
  return slot === 0 ? "product_shot" : "other";
}

interface TagSelectProps {
  /** Names the control: "Image 1 tag". */
  name: string;
  value: ProductImageTag;
  onChange: (tag: ProductImageTag) => void;
}

/**
 * A product image's tag as live picks it (design-system.md 3.6): a small button with the tag and a
 * chevron, opening a list of the four tags, each with what it is for.
 */
export function TagSelect({ name, value, onChange }: TagSelectProps) {
  const [opened, setOpened] = useState(false);
  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      width={458}
      position="bottom-start"
      shadow="sm"
      withinPortal
    >
      <Popover.Target>
        <UnstyledButton
          className={classes.target}
          aria-label={`${name}: ${TAG_LABEL[value]}`}
          aria-expanded={opened}
          data-expanded={opened || undefined}
          onClick={() => setOpened((open) => !open)}
        >
          <span className={classes.targetLabel}>{TAG_LABEL[value]}</span>
          <ChevronDown size={14} className={classes.chevron} aria-hidden />
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown className={classes.dropdown}>
        <div className={classes.list}>
          {Tags.options.map((tag) => (
            <UnstyledButton
              key={tag}
              aria-pressed={tag === value}
              className={classes.item}
              data-active={tag === value || undefined}
              onClick={() => {
                onChange(tag);
                setOpened(false);
              }}
            >
              <Text className={classes.title}>{TAG_LABEL[tag]}</Text>
              <Text className={classes.description}>{TAG_DESCRIPTION[tag]}</Text>
            </UnstyledButton>
          ))}
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
